import { forwardRef } from "react";
import type { DialogContentProps } from "@radix-ui/react-dialog";
import { Drawer } from "vaul";
import { cn } from "../lib/cn";

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Sheet heading; omit for content-only sheets. */
  title?: React.ReactNode;
  className?: string;
  /** Hide the default drag handle, e.g. when the content renders its own. */
  hideHandle?: boolean;
  /** Extra classes for the inner scrollable content wrapper. */
  contentClassName?: string;
  /**
   * Radix Dialog hooks, forwarded to the sheet content. Calling
   * `event.preventDefault()` on `onPointerDownOutside` blocks outside-dismiss.
   */
  onEscapeKeyDown?: DialogContentProps["onEscapeKeyDown"];
  onPointerDownOutside?: DialogContentProps["onPointerDownOutside"];
  /** Forwarded to the sheet content element. */
  "data-testid"?: string;
}

/**
 * Mobile bottom sheet over vaul's Drawer — drag handle, optional title and a
 * scrollable content area with safe-area bottom padding.
 */
export const BottomSheet = forwardRef<HTMLDivElement, BottomSheetProps>(
  (
    {
      open,
      onOpenChange,
      children,
      title,
      className,
      hideHandle = false,
      contentClassName,
      onEscapeKeyDown,
      onPointerDownOutside,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    return (
      <Drawer.Root open={open} onOpenChange={onOpenChange}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-(--z-modal) bg-surface-overlay" />
          <Drawer.Content
            ref={ref}
            data-slot="bottom-sheet"
            data-testid={dataTestId}
            onEscapeKeyDown={onEscapeKeyDown}
            onPointerDownOutside={onPointerDownOutside}
            className={cn(
              "fixed inset-x-0 bottom-0 z-(--z-modal) flex max-h-[85dvh] flex-col rounded-t-xl bg-surface-default outline-none",
              className,
            )}
          >
            {hideHandle ? null : (
              <Drawer.Handle className="mx-auto mt-3 h-1 w-10 rounded-full bg-border-strong" />
            )}
            {title ? (
              <Drawer.Title className="px-4 pt-3 type-h5 text-content-primary">
                {title}
              </Drawer.Title>
            ) : null}
            <div
              className={cn(
                "min-h-0 flex-1 overflow-auto px-4 pt-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))]",
                contentClassName,
              )}
            >
              {children}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    );
  },
);
BottomSheet.displayName = "BottomSheet";
